import React from "react";
import { useShallow } from "zustand/react/shallow";
import { useViewerStore } from "../../stores/viewerStore";
import {
  MOBILE_TOOLBAR_MEDIA_QUERY,
  VIEWPORT_TOOLBAR_POSITION_KEY,
  clampViewportToolbarPosition,
} from "./toolbarConstants";
import { ToolbarGroup } from "./toolbarUi";
import { priorityItems, visibilityItems } from "./toolbarItems";
import { useSceneToolbarPresence } from "./scenePresence";
import { SelectionPriorityPrimary, SelectionPrioritySubmenu } from "./SelectionPriorityTools";
import { TransformToolPrimary, TransformToolSubmenu } from "./TransformTools";
import { VisibilityPrimary, VisibilitySubmenu } from "./VisibilityTools";
import { useConstraintVisibilityUiSnapshot } from "./visibilityLabels";
import {
  useViewportCameraShortcuts,
  useViewportLayerShortcuts,
  useViewportScenePriorityShortcuts,
} from "./viewportToolbarShortcuts";

export default function ViewportToolbar() {
  useViewportScenePriorityShortcuts();
  useViewportLayerShortcuts();
  useViewportCameraShortcuts();

  const shellRef = React.useRef(null);
  const dragStateRef = React.useRef(null);
  const [openGroup, setOpenGroup] = React.useState(null);
  const [showCameras, setShowCameras] = React.useState(false);
  const [toolbarPosition, setToolbarPosition] = React.useState(null);
  const [isDragging, setIsDragging] = React.useState(false);
  React.useEffect(() => {
    try {
      window.localStorage.removeItem(VIEWPORT_TOOLBAR_POSITION_KEY);
    } catch {
      // Storage can be unavailable; the toolbar simply stays at its default location.
    }
  }, []);
  React.useEffect(() => {
    const handler = (event) => {
      setShowCameras((event?.detail?.visible ?? false) === true);
    };
    window.addEventListener("viewport-camera-visibility", handler);
    return () => window.removeEventListener("viewport-camera-visibility", handler);
  }, []);
  React.useEffect(() => {
    const onBlankTap = () => {
      if (!window.matchMedia(MOBILE_TOOLBAR_MEDIA_QUERY).matches) return;
      setOpenGroup(null);
    };
    window.addEventListener("mobile-viewport-blank-tap", onBlankTap);
    return () => window.removeEventListener("mobile-viewport-blank-tap", onBlankTap);
  }, []);
  const scenePresence = useSceneToolbarPresence();
  const layerVisibilityState = useViewerStore(
    useShallow((state) => ({
      showVisual: state.showVisual,
      showCollision: state.showCollision,
      showJoints: state.showJoints,
      showSites: state.showSites,
      showInertials: state.showInertials,
      showConstraintMarkers: state.showConstraintMarkers,
      showActuators: state.showActuators,
    }))
  );
  const constraintVisibilityUi = useConstraintVisibilityUiSnapshot();
  const visiblePriorityItems = React.useMemo(() => priorityItems, []);
  const visibleVisibilityItems = React.useMemo(() => visibilityItems, []);
  const visibleLabelKeys = React.useMemo(() => {
    const keys = ["body"];
    if (scenePresence.object) keys.push("object");
    visibleVisibilityItems.forEach((item) => keys.push(item.key));
    return keys;
  }, [scenePresence.object, visibleVisibilityItems]);
  const submenuItemCount =
    openGroup === "transform"
      ? 3
      : openGroup === "selection"
        ? visiblePriorityItems.length
        : openGroup === "visibility"
          ? visibleVisibilityItems.length
          : 0;

  const toggleGroup = React.useCallback((group) => {
    setOpenGroup((current) => (current === group ? null : group));
  }, []);
  const toggleShowCameras = React.useCallback(() => {
    const next = !showCameras;
    setShowCameras(next);
    window.dispatchEvent(
      new CustomEvent("viewport-camera-visibility", { detail: { visible: next } })
    );
  }, [showCameras]);
  const allVisibilityOff =
    !layerVisibilityState.showVisual &&
    !layerVisibilityState.showCollision &&
    !layerVisibilityState.showJoints &&
    !layerVisibilityState.showSites &&
    !layerVisibilityState.showInertials &&
    !showCameras &&
    !(
      layerVisibilityState.showConstraintMarkers &&
      constraintVisibilityUi.constraintsVisible !== false
    ) &&
    !(
      layerVisibilityState.showConstraintMarkers && constraintVisibilityUi.tendonsVisible !== false
    ) &&
    !layerVisibilityState.showActuators;

  React.useEffect(() => {
    if (!toolbarPosition) return;
    const handleResize = () => {
      const shell = shellRef.current;
      if (!shell) return;
      const rect = shell.getBoundingClientRect();
      setToolbarPosition((current) => {
        if (!current) return current;
        const next = clampViewportToolbarPosition(current, rect);
        return next;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [toolbarPosition]);

  const onGripPointerDown = React.useCallback((event) => {
    if (event.button !== 0) return;
    const shell = shellRef.current;
    if (!shell || window.matchMedia(MOBILE_TOOLBAR_MEDIA_QUERY).matches) return;
    const rect = shell.getBoundingClientRect();
    event.preventDefault();
    event.stopPropagation();
    setOpenGroup(null);
    dragStateRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    setToolbarPosition({ left: rect.left, top: rect.top });
    setIsDragging(true);
  }, []);

  React.useEffect(() => {
    if (!isDragging) return;
    const handlePointerMove = (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const next = clampViewportToolbarPosition(
        {
          left: event.clientX - dragState.offsetX,
          top: event.clientY - dragState.offsetY,
        },
        dragState
      );
      setToolbarPosition(next);
    };
    const finishDrag = () => {
      dragStateRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", finishDrag, { once: true });
    window.addEventListener("pointercancel", finishDrag, { once: true });
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", finishDrag);
      window.removeEventListener("pointercancel", finishDrag);
    };
  }, [isDragging]);

  return (
    <div
      ref={shellRef}
      className={`viewport-toolbar-shell${toolbarPosition ? " viewport-toolbar-shell-positioned" : ""}${isDragging ? " viewport-toolbar-shell-dragging" : ""}${openGroup ? ` viewport-toolbar-shell-open viewport-toolbar-shell-${openGroup}` : ""}`}
      style={{
        "--viewport-toolbar-submenu-item-count": Math.max(1, submenuItemCount),
        "--viewport-toolbar-submenu-gap-count": Math.max(0, submenuItemCount - 1),
        ...(toolbarPosition
          ? {
              left: `${toolbarPosition.left}px`,
              top: `${toolbarPosition.top}px`,
              bottom: "auto",
            }
          : null),
      }}
      role="toolbar"
      aria-label="Viewport tools"
    >
      <button
        type="button"
        className="viewport-toolbar-grip"
        aria-label="Drag toolbar"
        onPointerDown={onGripPointerDown}
      >
        ⋮⋮
      </button>
      <div className="viewport-toolbar-primary-row">
        <ToolbarGroup
          label="Transform tools"
          muted={openGroup !== null && openGroup !== "transform"}
          primary={
            <TransformToolPrimary
              active={openGroup === "transform"}
              onClick={() => toggleGroup("transform")}
            />
          }
        />
        <ToolbarGroup
          label="Selection priority"
          muted={openGroup !== null && openGroup !== "selection"}
          primary={
            <SelectionPriorityPrimary
              active={openGroup === "selection"}
              items={visiblePriorityItems}
              onClick={() => toggleGroup("selection")}
            />
          }
        />
        <ToolbarGroup
          label="Viewer layers"
          muted={openGroup !== null && openGroup !== "visibility"}
          primary={
            <VisibilityPrimary
              active={openGroup === "visibility"}
              allVisibilityOff={allVisibilityOff}
              labelKeys={visibleLabelKeys}
              onClick={() => toggleGroup("visibility")}
            />
          }
        />
      </div>
      {openGroup && <div className="viewport-toolbar-divider" aria-hidden />}
      {openGroup && (
        <div
          className={`viewport-toolbar-submenu viewport-toolbar-submenu-${openGroup}`}
          aria-label={`${openGroup} options`}
        >
          {openGroup === "transform" && <TransformToolSubmenu />}
          {openGroup === "selection" && (
            <SelectionPrioritySubmenu items={visiblePriorityItems} scenePresence={scenePresence} />
          )}
          {openGroup === "visibility" && (
            <VisibilitySubmenu
              items={visibleVisibilityItems}
              scenePresence={scenePresence}
              showCameras={showCameras}
              onToggleCameras={toggleShowCameras}
            />
          )}
        </div>
      )}
    </div>
  );
}
